import * as React from 'react';
import clone from 'lodash/clone';
import isEmpty from 'lodash/isEmpty';

import { __t, applyTranslations } from 'utils/language';

import './ccm-campaign-overview.scss';

applyTranslations(require('./ccm-campaign-overview.i18n.json'));

import type { Campaign } from 'types';

type Props = {
  campaign?: Campaign,
  isCampaignCreatorless?: boolean
}

const MSG_KEY = 'ccm_campaign_overview';
const COMMON_MSG_KEY = 'collabary_common';

const CcmCampaignMainInfo = ({ campaign, isCampaignCreatorless }: Props) => {
  const renderCountries = (): React.ReactNode => {
    const countries = (campaign?.countries || [])
      .map(country => country.toUpperCase())
      .sort();

    return (
      <div className="ccm-campaign-overview__countries">
        {
          countries.map(country => (
            <div
              key={`ccm-campaign-overview__country--${country}`}
              className="ccm-campaign-overview__country"
            >
              {country}
            </div>
          ))
        }
      </div>
    );
  };

  const renderTags = (): React.ReactNode => {
    const tags = clone(campaign?.fashionStyles || [])
      .sort();

    return (
      <div className="ccm-campaign-overview__tags">
        {
          tags.map(tag => (
            <div
              key={`ccm-campaign-overview__tag--${tag}`}
              className="ccm-campaign-overview__tag"
            >
              {__t(`${COMMON_MSG_KEY}.fashion_styles.${tag}`)}
            </div>
          ))
        }
      </div>
    );
  };

  const renderTargetGroup = (): React.ReactNode => (
    <div className="ccm-campaign-overview__target-group">
      {(campaign?.genders || []).map(gender => __t(`${MSG_KEY}.${gender}`)).join()}
    </div>
  );

  const getZalandoLink = (): React.ReactNode => (
    <a
      href={campaign?.collectionLink}
      rel="noopener noreferrer"
      target="_blank"
      className="ccm-campaign-overview__link"
    >
      {campaign?.collectionLink ?? ''}
    </a>
  );

  const getSection = (title: string, value: React.ReactNode): React.ReactNode => (
    <div className="ccm-campaign-overview__section">
      <div className="ccm-campaign-overview__section-title">
        {title}
      </div>
      <div className="ccm-campaign-overview__section-value">
        {value}
      </div>
    </div>
  );

  if (isCampaignCreatorless) {
    return (
      <div>
        <div className="ccm-campaign-overview__title">
          {__t(`${MSG_KEY}.campaign_description`)}
        </div>
        <div className="ccm-campaign-overview__row-section">
          {getSection(__t(`${MSG_KEY}.section_titles.target_group`), renderTargetGroup())}
          {getSection(__t(`${MSG_KEY}.section_titles.published_countries`), renderCountries())}
        </div>
        {getSection(__t(`${MSG_KEY}.section_titles.zalando_link`), getZalandoLink())}
      </div>
    );
  }

  return (
    <div>
      <div className="ccm-campaign-overview__sections">
        {getSection(__t(`${MSG_KEY}.section_titles.collection_link`), getZalandoLink())}
        {getSection(__t(`${MSG_KEY}.section_titles.published_countries`), renderCountries())}
        {getSection(__t(`${MSG_KEY}.section_titles.target_group`), renderTargetGroup())}
        {getSection(__t(`${MSG_KEY}.section_titles.tags`), renderTags())}
        {
          !isEmpty(campaign?.excludedBrands) && (
          <div className="ccm-campaign-overview__section">
            <div className="ccm-campaign-overview__section-title">
              {__t(`${MSG_KEY}.section_titles.excluded_brands`)}
            </div>
            <div className="ccm-campaign-overview__section-value">
              {clone(campaign?.excludedBrands || []).sort().join('; ')}
            </div>
          </div>
          )
          }
      </div>
    </div>
  );
};

export default CcmCampaignMainInfo;
